import { useReducer, useEffect, useState, useMemo } from "react";
import { Grid, Button, Typography } from "@mui/material";
import { Box } from "@mui/system";
import T from "utils/constants/T";
import {
  StyledBodyTypography,
  StyledTitleTypography,
} from "../../../CompanyDetailsScreen/CompanyDetails/CompanyTab/CompanyInfo";
import CashDCard from "components/common/CashDCard";
import { useSelector } from "store";
import MainCard from "components/MainCard";
import CashDDialog from "components/common/CashDDialog";
import CashDAutocomplete from "components/common/CashDAutocomplete";
import CashDTextField from "components/common/CashDTextfield";
import { useDispatch } from "react-redux";
import {
  getUserByUserID,
  saveEmployeeSetting,
} from "store/reducers/getEmployeeData";
import { useNavigate, useParams } from "react-router";
import { getPaymentPartnersById } from "store/reducers/companyBankingSettings";

const EditEmployeeSettings = () => {
  const dispatch = useDispatch();
  const { employeeID } = useParams();
  const navigate = useNavigate();
  const getUserDataByUSerID = useSelector((state) => state.getEmployeeData);
  const { paymentPartner } = useSelector(
    (state: any) => state.bankingSettingsTabData
  );
  const [errors, setErrors] = useState({
    minError: "",
    maxError: "",
    maxPerErr: "",
  });
  const { currencySymbol, paymentSystemId } = useSelector(
    (state: any) => state.companyTabData
  );

  const cs = useMemo(
    () => (currencySymbol ? currencySymbol : T.DOLLAR),
    [currencySymbol]
  );
  const TRANSACTION_FEE_DEDUCTION_TYPES = [cs, T.PERCENTAGE];
  const [localState, setLocalState] = useReducer(
    (prevState: any, newState: any) => ({ ...prevState, ...newState }),
    {
      maximumWithdrawPer: getUserDataByUSerID?.maxWithdrawalPercent || 0,
      maxWithdrawalMoney: getUserDataByUSerID?.maxWithdrawalMoney || 0,
      minimumWithdrawal: getUserDataByUSerID?.minWithdrawal || 0,
      currencyType: getUserDataByUSerID?.maxType ? T.PERCENTAGE : cs,
      limitMoney: getUserDataByUSerID?.maxType ? true : false,
      opneDeleteLogoConfirmationModal: false,
    }
  );
  const {
    maximumWithdrawPer,
    maxWithdrawalMoney,
    minimumWithdrawal,
    currencyType,
    // limitMoney,
    opneDeleteLogoConfirmationModal,
  } = localState;

  useEffect(() => {
    if (paymentSystemId) {
      dispatch(getPaymentPartnersById(paymentSystemId));
    }
  }, [dispatch, paymentSystemId]);

  const payload = {
    is_limit_money_max_wallet: currencyType === T.PERCENTAGE ? true : false,
    min_withdrawal: minimumWithdrawal,
    limit_allowable_percent_drawdown: maximumWithdrawPer,
    limit_money: maxWithdrawalMoney,
  };

  const staffID = getUserDataByUSerID.staffID;
  const handleSave = async () => {
    const res: any = await dispatch(saveEmployeeSetting(staffID, payload)); // NOSONAR
    if (res.code === 200) {
      await dispatch(getUserByUserID(employeeID)); // NOSONAR
      navigate(`/employees/employeeDetails/${employeeID}/settings/view`);
    }
  };
  const handleCancel = () => {
    handleCancelConfirmationModal();
  };

  const handleCancelConfirmationModal: any = () => {
    setLocalState({
      opneDeleteLogoConfirmationModal: !opneDeleteLogoConfirmationModal,
    });
  };

  const handleCancelConfirmation = async () => {
    setLocalState({
      maximumWithdrawPer: getUserDataByUSerID.maxWithdrawalPercent,
      maxWithdrawalMoney: getUserDataByUSerID.maxWithdrawalMoney,
      minimumWithdrawal: getUserDataByUSerID.minWithdrawal,
      currencyType: getUserDataByUSerID.maxType ? T.PERCENTAGE : T.DOLLAR,
      limitMoney: getUserDataByUSerID.maxType ? true : false,
      opneDeleteLogoConfirmationModal: !opneDeleteLogoConfirmationModal,
    });
    navigate(`/employees/employeeDetails/${employeeID}/settings/view`);
  };
  const onHandleChangeMax = () => {
    const num = Number(localState.maxWithdrawalMoney);
    const min = Number(localState.minimumWithdrawal);

    let maxError = "";
    if (num < 0 && currencyType !== T.PERCENTAGE) {
      maxError = "Maximum withdrawal cannot be negative";
    } else if (num < min) {
      maxError = "Maximum must be greater than or equal to Minimum";
    }

    setErrors((prev) => ({
      ...prev,
      maxPerErr: currencyType !== T.PERCENTAGE ? "" : prev.maxPerErr,
      maxError,
    }));
  };

  const onHandleChangeMin = () => {
    const num = Number(localState.minimumWithdrawal);
    const max = Number(localState.maxWithdrawalMoney);

    let minError = "";
    if (num < 0) {
      minError = "Minimum withdrawal cannot be negative";
    } else if (paymentPartner === T.NIUM ? num <= 2 : num <= 0) {
      minError =
        paymentPartner === T.NIUM
          ? "Minimum withdrawal must be greater than 2"
          : "Minimum withdrawal must be greater than 0";
    } else if (currencyType !== T.PERCENTAGE && num > max) {
      minError = "Minimum cannot be greater than Maximum";
    }

    setErrors((prev) => ({ ...prev, minError }));
  };

  const onHandleChangeMaxPer = () => {
    const num = Number(localState.maximumWithdrawPer);
    let maxPerErr = "";
    if (num <= 0) {
      maxPerErr = "Maximum withdrawal must be grater than 0";
    } else if (num > 100) {
      maxPerErr = "Maximum cannot be greater than 100%";
    }

    setErrors((prev) => ({
      ...prev,
      maxError: currencyType === T.PERCENTAGE ? "" : prev.maxError,
      maxPerErr,
    }));
  };

  const onHandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocalState({ [name]: value });
  };

  useEffect(() => {
    onHandleChangeMin();
    onHandleChangeMax();
    onHandleChangeMaxPer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localState.minimumWithdrawal,
    localState.maxWithdrawalMoney,
    localState.maximumWithdrawPer,
    paymentPartner,
    currencyType,
  ]);

  const onHandleAutoCompleteChange = (type: string, newValue: any) => {
    const newLimitMoney = newValue !== "%" ? true : false;
    setLocalState({ [type]: newValue, limitMoney: newLimitMoney });
    setLocalState({
      maxWithdrawalMoney: getUserDataByUSerID.maxWithdrawalMoney,
      minimumWithdrawal: getUserDataByUSerID.minWithdrawal,
    });
    setErrors({ minError: "", maxError: "", maxPerErr: "" });
    if (
      getUserDataByUSerID.maxWithdrawalMoney < getUserDataByUSerID.minWithdrawal
    ) {
      setErrors((prev) => ({
        ...errors,
        maxError: "Maximum must be greater than or equal to Minimum",
      }));
    }
  };

  return (
    <MainCard
      title="          "
      secondary={
        <>
          <Button
            variant="outlined"
            onClick={handleCancel}
            className="main-card-button"
          >
            {T.CANCEL}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            // className='main-card-button'
            style={{ marginLeft: "8px" }}
            disabled={
              !(!errors.minError && !errors.maxError && !errors.maxPerErr)
            }
          >
            {T.SAVE}
          </Button>
        </>
      }
    >
      <Grid item xs={12}>
        <CashDCard borderRadius="20px">
          <Grid container rowSpacing={2}>
            <Grid item xs={6}>
              <Grid container>
                <Grid item xs={4}>
                  <StyledTitleTypography>
                    {T.EMPLOYEE_NAME}
                  </StyledTitleTypography>
                </Grid>
                <Grid item xs={8}>
                  <StyledBodyTypography>
                    {getUserDataByUSerID.employeeName}
                  </StyledBodyTypography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container>
                <Grid item xs={4}>
                  <StyledTitleTypography>
                    {T.EMPLOYEE_EMAIL}
                  </StyledTitleTypography>
                </Grid>
                <Grid item xs={8}>
                  <StyledBodyTypography>
                    {getUserDataByUSerID.email}
                  </StyledBodyTypography>
                </Grid>
              </Grid>
            </Grid>
            <Grid className="divider" item xs={12}></Grid>
            <Grid item xs={6}>
              <Grid container>
                <Grid item xs={4}>
                  <StyledTitleTypography>
                    {T.MAXIMUM_WITHDRAWAL}
                  </StyledTitleTypography>
                </Grid>
                <Grid item xs={8}>
                  <Grid container spacing={1}>
                    <Grid item xs={3}>
                      <CashDAutocomplete
                        placeholder=""
                        fullWidth
                        displayRemoveButton="none"
                        listDetails={TRANSACTION_FEE_DEDUCTION_TYPES}
                        value={currencyType}
                        getByLabelText={(option: any) => option}
                        onHandleChange={(event: any, newValue: any) =>
                          onHandleAutoCompleteChange("currencyType", newValue)
                        }
                      />
                    </Grid>
                    {currencyType === T.PERCENTAGE ? (
                      <Grid item xs={7.5}>
                        <CashDTextField
                          fullWidth
                          placeholder={T.PERCENTAGE}
                          autoComplete="on"
                          type="number"
                          size="medium"
                          variant="outlined"
                          name="maximumWithdrawPer"
                          value={maximumWithdrawPer}
                          onChange={onHandleChange}
                          error={!!errors.maxPerErr}
                        />
                        {errors.maxPerErr && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ marginTop: "4px", display: "block" }}
                          >
                            {errors.maxPerErr}
                          </Typography>
                        )}
                      </Grid>
                    ) : (
                      <Grid item xs={7.5}>
                        <CashDTextField
                          fullWidth
                          placeholder={T.MAXIMUM_WITHDRAWAL_MONEY}
                          autoComplete="on"
                          type="number"
                          size="medium"
                          variant="outlined"
                          name="maxWithdrawalMoney"
                          value={localState.maxWithdrawalMoney}
                          onChange={onHandleChange}
                          error={!!errors.maxError}
                        />
                        {errors.maxError && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ marginTop: "4px", display: "block" }}
                          >
                            {errors.maxError}
                          </Typography>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container>
                <Grid item xs={4}>
                  <StyledTitleTypography>
                    {`${T.MINIMUM_WITHDRAWAL} (${cs})`}
                  </StyledTitleTypography>
                </Grid>
                <Grid item xs={6}>
                  <CashDTextField
                    fullWidth
                    placeholder={T.MINIMUM_WITHDRAWAL}
                    autoComplete="on"
                    type="number"
                    size="medium"
                    variant="outlined"
                    name="minimumWithdrawal"
                    value={minimumWithdrawal}
                    onChange={onHandleChange}
                    error={!!errors.minError}
                  />
                  {errors.minError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ marginTop: "4px", display: "block" }}
                    >
                      {errors.minError}
                    </Typography>
                  )}
                </Grid>
                {/* <Grid item xs={2}/> */}
              </Grid>
            </Grid>
          </Grid>
        </CashDCard>
        <CashDDialog
          open={opneDeleteLogoConfirmationModal}
          handleClose={handleCancelConfirmationModal}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              width: 450,
              textAlign: "center",
            }}
          >
            {`${T.ARE_YOU_SURE_YOU_WANT_TO_CANCEL_THE_CHANGES}`}
          </Typography>
          <Box className="confirmation_modal">
            <Button variant="outlined" onClick={handleCancelConfirmationModal}>
              {T.NO}
            </Button>
            <Button variant="contained" onClick={handleCancelConfirmation}>
              {T.YES}
            </Button>
          </Box>
        </CashDDialog>
      </Grid>
    </MainCard>
  );
};

export default EditEmployeeSettings;